import axios from 'axios';

const cache = {};

/** Element UI el-tag 预设类型 */
export const DICT_TAG_TYPES = [
  {value: '', label: '默认'},
  {value: 'primary', label: '主要'},
  {value: 'success', label: '成功'},
  {value: 'info', label: '信息'},
  {value: 'warning', label: '警告'},
  {value: 'danger', label: '危险'}
];

/** 纯文字颜色（与 Element UI 标签色一致） */
export const DICT_TEXT_COLOR_MAP = {
  '': '#606266',
  primary: '#409EFF',
  success: '#67C23A',
  info: '#909399',
  warning: '#E6A23C',
  danger: '#F56C6C'
};

/**
 * 纯文字样式（无背景）
 */
export function getDictTextOnlyStyle(type) {
  const key = type === undefined || type === null ? '' : String(type);
  return {
    color: DICT_TEXT_COLOR_MAP[key] || DICT_TEXT_COLOR_MAP[''],
    backgroundColor: 'transparent',
    borderColor: 'transparent'
  };
}

/**
 * 解析字典样式为 el-tag 可用参数
 * @param {string} listClass 预设类型、#背景色 或 text:预设类型
 */
export function resolveDictTag(listClass) {
  const styleClass = (listClass || '').trim();
  // Element UI 空 type 视觉接近 primary，默认改为 info（灰色）以区分「主要」
  if (!styleClass) {
    return {type: 'info', style: {}};
  }
  if (styleClass.indexOf('text:') === 0) {
    const textValue = styleClass.slice(5).trim();
    if (textValue.charAt(0) === '#') {
      return {
        type: '',
        style: {
          color: textValue,
          backgroundColor: 'transparent',
          borderColor: 'transparent'
        }
      };
    }
    return {
      type: '',
      style: getDictTextOnlyStyle(textValue)
    };
  }
  if (styleClass.charAt(0) === '#') {
    return {
      type: '',
      style: {
        backgroundColor: styleClass,
        borderColor: styleClass,
        color: '#fff'
      }
    };
  }
  return {type: styleClass, style: {}};
}

/**
 * 按字典编码加载字典项（带前端内存缓存）
 */
export function getDictData(dictCode) {
  if (cache[dictCode]) {
    return Promise.resolve(cache[dictCode]);
  }
  return axios.get('/sys/dictdata', {
    params: {dictCode}
  }).then(res => {
    if (res.data.code === 0) {
      cache[dictCode] = res.data.data || [];
      return cache[dictCode];
    }
    return Promise.reject(new Error(res.data.msg || '加载字典失败'));
  });
}

/**
 * 获取字典项对象
 * @param {string} dictCode 字典编码
 * @param {string|number} value 字典项值
 * @param {string} field 分组字段，如 order_status
 */
export function getDictItem(dictCode, value, field) {
  const list = cache[dictCode] || [];
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const valueStr = String(value);
  const fieldStr = field ? String(field).trim() : '';

  if (fieldStr) {
    const compositeCode = fieldStr + '_' + valueStr;
    const compositeItem = list.find(row => String(row.dictDataCode) === compositeCode);
    if (compositeItem) {
      return compositeItem;
    }
    const groupedItem = list.find(row => {
      const note = String(row.dictDataNote || row.note || '').trim();
      return String(row.dictDataCode) === valueStr && note === fieldStr;
    });
    if (groupedItem) {
      return groupedItem;
    }
  }

  // 优先按 code 匹配，避免 dictDataId 与业务状态值撞号导致显示错乱
  const byCode = list.find(row => String(row.dictDataCode) === valueStr);
  if (byCode) {
    return byCode;
  }
  return list.find(row => String(row.dictDataId) === valueStr) || null;
}

/**
 * 根据字典项 ID 或 code 获取显示名称
 */
export function getDictLabel(dictCode, value, field) {
  const item = getDictItem(dictCode, value, field);
  return item ? item.dictDataName : '';
}

/**
 * 根据字典项 ID 或 code 获取样式
 */
export function getDictListClass(dictCode, value, field) {
  const item = getDictItem(dictCode, value, field);
  return item ? item.listClass : undefined;
}

/**
 * 清除前端字典缓存
 */
export function clearDictCache(dictCode) {
  if (dictCode) {
    delete cache[dictCode];
    return;
  }
  Object.keys(cache).forEach(key => {
    delete cache[key];
  });
}

/**
 * 字典列表转 el-option 数据
 * @param {Array} list getDictData 返回值
 * @param {{numberValue?: boolean}} opts numberValue=true 时 value 转为数字
 */
export function mapDictOptions(list, opts = {}) {
  const numberValue = !!opts.numberValue;
  return (list || []).map((item) => {
    const code = item.dictDataCode;
    return {
      value: numberValue && code !== '' && !Number.isNaN(Number(code)) ? Number(code) : code,
      label: item.dictDataName,
      listClass: item.listClass
    };
  });
}

/**
 * 加载字典并转为 options（封装常用写法）
 */
export function loadDictOptions(dictCode, opts = {}) {
  return getDictData(dictCode).then((list) => mapDictOptions(list, opts));
}
